<template>
  <div>
    <div class="row">
      <div class="col-12 col-xl-6">
        <search-box v-if="!hideSearchInput" />
      </div>

      <div v-if="!hideAddButton" class="col-12 col-sm-3 col-xl-2 ml-auto text-right">
        <check-permission :permissions="addPermissions" :entity-type="addEntityType" :entity-id="addButtonEntityId">
          <button aria-label="Dodaj" class="btn btn-primary btn-block" @click.prevent="$emit('add')">
            <i class="fal fa-fw fa-plus mr-1 d-none d-lg-inline-block" />

            <span>{{ addButtonText }}</span>
          </button>
        </check-permission>
      </div>
    </div>

    <div v-if="$slots.filters" class="row mb-3">
      <div class="col-12">
        <slot name="filters" />
      </div>
    </div>

    <slot v-if="showCheckboxes" name="showCheckboxesButtons" />

    <div v-if="showTotal" class="col-12">
      <b>{{ totalText }}</b> {{ total }}
    </div>

    <div class="table-responsive mt-2">
      <table class="table table-bordered table-hover table-striped w-100 dataTable dtr-inline">
        <t-head
          :show-checkbox="showCheckboxes"
          :columns="columns"
          :sort="sort"
          :actions="actions"
          :initial-checked="allChecked"
          :show-column-numbers="showColumnNumbers"
          @order="order"
          @checkAll="checkAll"
        />

        <tbody>
          <t-row
            v-for="(object, index) in data"
            :key="object[idKey] ? object[idKey] + '_' + index : object.name + index"
            :show-checkbox="showCheckboxes"
            :entity-type="entityType"
            :object="object"
            :object-index="index"
            :columns="columns"
            :actions="actions"
            :id-key="idKey"
            v-on="$listeners"
          />
        </tbody>
      </table>
    </div>

    <div v-if="pages" class="text-right mt-2">
      <div class="d-inline-block">
        <div class="form-inline">
          <button class="btn btn-light" :disabled="selectedPage <= 1" @click.prevent="previousPage">
            <i class="fas fa-chevron-left" />
          </button>

          <div class="form-group mx-2">
            <input
              v-model="page"
              min="1"
              class="form-control page-input text-center"
              type="number"
              :max="pages"
              :size="pages.toString().length"
              @blur="checkPage"
              @keyup.enter="checkPage"
            />

            <span class="mx-2">z</span>

            <span>{{ pages }}</span>
          </div>

          <button class="btn btn-light" :disabled="selectedPage >= pages" @click.prevent="nextPage">
            <i class="fas fa-chevron-right" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SearchBox from '@/components/helpers/SearchBox';
import THead from './components/THead';
import TRow from './components/TRow';

import { DEFAULT_COUNT } from '@/utils/constants';

// @vuese
// Domyślna tabela
// @group Table
export default {
  name: 'Table',
  components: { SearchBox, THead, TRow },
  props: {
    // Tablica zawierająca obiekty
    // {
    //   name : Nazwa property do wyświetlenia
    //   nameTransformer: funkcja transformująca wartość
    //   displayName: Nazwa do wyświetlenia w nagłówku tabeli
    //   minWidth : string - minimalna szerokość kolumny w dowolnej jednostce
    //   width : string - szerokość kolumny w dowolnej jednostce
    //   sortable : boolean - możliwość sortowania po danej kolumnie
    //   sortBy : Nazwa property do sortowania, jeśli brak, to nazwa property jest brana z pola name
    // }
    columns: { type: Array, required: true },

    // Lista obiektów do wyświetlenia w tabeli
    // Property obiektu musi pasować do name w columns
    data: { type: Array, required: true },

    // Typ encji wyświetlanej w tabeli
    entityType: { type: String, required: false },

    // Całkowita liczba obiektów wyświetlanych na liście
    total: { type: Number, required: true },

    // Lista obiektów akcji do wykorzystania
    // { event: 'Nazwa eventu do złapania', name: 'Nazwa do wyświetlenia w przycisku' }
    actions: { type: Array, required: false, default: () => [] },

    // Liczba obiektów wyświetlanych na stronie
    perPage: { type: Number, required: false, default: DEFAULT_COUNT },

    // Klucz identyfikatora
    idKey: { type: String, required: false, default: 'id' },

    // Możliwość edycji wielu wierszy na raz
    showCheckboxes: { type: Boolean, required: false, default: false },

    // Czy wyświetlić input z wyszukiwaniem
    hideSearchInput: { type: Boolean, required: false, default: false },

    // Czy ukryć przycisk dodawania
    hideAddButton: { type: Boolean, required: false, default: false },

    // Tekst przycisku do dodawania
    addButtonText: { type: String, required: false, default: 'Dodaj' },

    // Nadpisanie uprawnień przycisku dodawania
    addButtonPermissions: { type: String, required: false },

    // Nadpisanie enitityId  przycisku dodawania
    addButtonEntityId: { type: [String, Number], required: false },

    // Nadpisanie entityType  przycisku dodawania
    addButtonEntityType: { type: String, required: false },

    // Czy pokazywać numery kolumn
    showColumnNumbers: { type: Boolean, default: false },

    // Czy pokazywać podsumowanie
    showTotal: { type: Boolean, default: false },

    // Tekst podsumowania
    totalText: { type: String, default: 'Wszystkie:' }
  },
  data() {
    return {
      sort: this.$route.query ? { order: this.$route.query.order, desc: this.$route.query.desc === 'true' } : undefined,
      search: this.$route.query.search || '',
      page: 1
    };
  },
  computed: {
    pages() {
      if (this.perPage <= 0) {
        return 0;
      }
      return Math.ceil(this.total / this.perPage) || 1;
    },
    selectedPage() {
      const start = parseInt(this.$route.query.start);
      const count = parseInt(this.$route.query.count) || this.perPage;
      if (start >= 0 && count >= 0) {
        return Math.floor(start / count) + 1;
      }
      return 1;
    },
    addEntityType() {
      return this.addButtonEntityType || this.entityType;
    },
    addPermissions() {
      return this.addButtonPermissions || (this.entityType && this.entityType.toUpperCase() + '.ADD');
    },
    allChecked() {
      return this.data.every(item => !!item.checked);
    }
  },
  watch: {
    selectedPage(val) {
      this.page = val;
    }
  },
  mounted() {
    this.page = this.selectedPage;
  },
  methods: {
    // @vuese
    // Ustawia stronę
    setPage(page) {
      this.reload({ ...this.$route.query, count: this.perPage, start: (page - 1) * this.perPage });
    },
    // @vuese
    // Następna strona
    nextPage() {
      if (this.checkPage()) {
        this.setPage(this.selectedPage + 1);
      }
    },
    // @vuese
    // Poprzednia strona
    previousPage() {
      if (this.checkPage()) {
        this.setPage(this.selectedPage - 1);
      }
    },
    // @vuese
    // Sprawdzanie wpisanej strony
    checkPage() {
      let page = parseInt(this.page);
      if (page < 1) {
        this.page = page = 1;
      }
      if (page > this.pages) {
        this.page = page = this.pages;
      }
      if (page !== this.selectedPage) {
        this.setPage(page);
        return false;
      }
      return true;
    },
    // @vuese
    // Czyszczenie pola search
    clearSearch() {
      this.search = '';
      this.reload({ ...this.$route.query, search: undefined });
    },
    // @vuese
    // Ustawia wyszukiwaną frazę
    setSearch(search) {
      this.reload({ ...this.$route.query, search });
    },
    // @vuese
    // Zmiana sortowania tabeli
    order(order) {
      this.sort = order;
      this.reload({ ...this.$route.query, ...order });
    },
    // @vuese
    // Przeładowuje stronę
    reload(query) {
      const nextRoute = { name: this.$route.name, params: this.$route.params, query };
      this.$router.replace(nextRoute);
    },
    // @vuese
    // Zaznacza wszystkie obiekty z listy
    checkAll(value) {
      this.$emit('checkAll', value);
    }
  }
};
</script>

<style>
input.page-input::-webkit-outer-spin-button,
input.page-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input.page-input[type='number'] {
  -moz-appearance: textfield;
}

.table-responsive {
  overflow: auto;
  max-height: 55vh;
  position: relative;
}

.table-responsive thead th,
.table-responsive thead .pseudo-th {
  position: sticky;
  top: -5px;
}
</style>
